import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound, Lock, RefreshCw, LogIn } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SensitiveContentGuard = ({ children, user }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);
  const [step, setStep] = useState(1); // 1: Send Code, 2: Verify

  useEffect(() => {
    if (timer > 0) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [timer]);

  const handleRequestOTP = async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-otp`, { email: user.email });
      setTimer(60);
      setStep(2);
      toast.success('Verification code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!user?.email) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/verify-sensitive`, 
        { email: user.email, otp },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsVerified(true);
      toast.success('Access Granted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  if (isVerified) {
    return children;
  }

  return (
    <div className="relative min-h-[600px] w-full">
      {/* Blurred background content holder (placeholder or children blurred) */}
      <div className="absolute inset-0 filter blur-md bg-white/30 pointer-events-none">
        {/* We can render children or just a skeleton here to make it look like blurred content */}
        <div className="p-8 space-y-6">
          <div className="h-8 bg-stone-200 rounded w-1/4"></div>
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-stone-200 rounded-xl"></div>)}
          </div>
          <div className="h-96 bg-stone-100 rounded-2xl"></div>
        </div>
      </div>

      {/* Overlay Modal */}
      <div className="absolute inset-0 flex items-center justify-center bg-stone-900/40 backdrop-blur-sm z-[45]">
        <div className="w-full max-w-md bg-white rounded-2xl border border-stone-200 shadow-2xl p-8 m-4">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-amber-200">
              <Lock className="text-amber-600" size={24} />
            </div>
            <h2 className="text-xl font-bold text-[#1c1917] mb-1">Security Verification</h2>
            <p className="text-sm text-stone-500">
              {step === 1 
                ? 'This area contains sensitive data. Click below to receive a verification code.' 
                : `Please verify your identity with the code sent to ${user?.email}`}
            </p>
          </div>

          {step === 1 ? (
            <div className="space-y-4">
              <Button
                onClick={handleRequestOTP}
                disabled={loading}
                className="w-full h-12 rounded-xl bg-[#022c22] text-[#d4af37] hover:bg-[#064e3b] font-bold text-sm shadow-lg transition-all flex items-center justify-center gap-2"
              >
                {loading ? <RefreshCw className="animate-spin" size={20} /> : (
                  <>
                    <LogIn size={18} />
                    <span>Send Verification Code</span>
                  </>
                )}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleVerify} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-stone-600 text-xs font-bold uppercase tracking-wider">Verification Code</Label>
                <div className="relative group">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-[#022c22] js-focus" size={18} />
                  <Input
                    id="otp"
                    type="text"
                    maxLength={6}
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    required
                    autoFocus
                    className="pl-10 h-12 bg-stone-50 border-stone-200 focus:border-[#022c22] focus:ring-1 focus:ring-[#022c22]/20 text-center text-xl font-bold tracking-[0.5em]"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || otp.length < 6}
                className="w-full h-12 rounded-xl bg-[#022c22] text-[#d4af37] hover:bg-[#064e3b] font-bold text-sm shadow-lg transition-all flex items-center justify-center gap-2"
              >
                {loading ? <RefreshCw className="animate-spin" size={20} /> : (
                  <>
                    <LogIn size={18} />
                    <span>Verify & Unlock</span>
                  </>
                )}
              </Button>

              <div className="flex flex-col items-center gap-2 text-center">
                <button 
                  type="button"
                  onClick={handleRequestOTP}
                  disabled={loading || timer > 0}
                  className={`text-sm font-medium transition-colors ${timer > 0 ? 'text-stone-400 cursor-not-allowed' : 'text-[#022c22] hover:underline'}`}
                >
                  {timer > 0 ? `Resend code in ${timer}s` : 'Did not receive a code? Resend'}
                </button>
                
                <button 
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-xs text-stone-400 hover:text-stone-600 transition-colors uppercase tracking-widest"
                >
                  Back
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SensitiveContentGuard;
